import EditOutlined from '@mui/icons-material/EditOutlined';
import { Grid, IconButton } from '@mui/material';
import React, { HTMLAttributes, useEffect, useState } from 'react';

import { EditNodePartLiveSearchProps } from './EditNodePartLiveSearch.props';
import { LocationNodeData, NodeType } from '../../../@types';
import { Node, PartLocationType, SearchCode } from '../../../@types/part.type';
import { SAMPLE_PART_LOCATION } from '../../../constants/part';
import { useSearchNodesParts, useSearchParts } from '../../../queries/live-search-query';
import { displayNodeTypeName } from '../../../utils/node-type';
import { Button, ButtonColorProps, ButtonSizeProps, ButtonVariantProps } from '../../atoms/Button';
import { LiveSearchBox, LiveSearchListItem } from '../../atoms/LiveSearchBox';
import { PermissionLink } from '../../atoms/PermissionLink';
import { Typography, TypographyVariantProps } from '../../atoms/Typography';
import { LocationSearchItem } from '../../molecules/LocationSearchItem';

export const EditNodePartLiveSearch: React.FC<EditNodePartLiveSearchProps> = ({
  isLocation,
  isClientHierarchy,
  isEditable,
  isSerial = false,
  partLocation = SAMPLE_PART_LOCATION,
  serial = '',
  title = 'Part/Node Search',
  handlePartLocationChange,
  onChange,
  searchLimit,
  applicableNodeIds,
  isUpdatePartLocation,
  alwaysShowEditIcon = false,
}) => {
  const [isShown, setIsShown] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [locationId, setLocationId] = useState(0);
  const [locationName, setLocationName] = useState(partLocation.name || '');
  const [locationHierarchy, setLocationHierarchy] = useState('');
  const [locationTypeCode, setLocationTypeCode] = useState('');
  const [textValue, setTextValue] = useState('');
  const [id, setId] = useState(0);

  const searchNodeParts = useSearchNodesParts();
  const searchParts = useSearchParts(searchLimit);

  useEffect(() => {
    serial !== textValue && setTextValue(serial);
  }, [serial]);

  const handleOnSubmit = () => {
    onChange && onChange(textValue, id);
    handlePartLocationChange &&
      handlePartLocationChange({
        ...partLocation,
        locationId: locationId === 0 ? partLocation.locationId : locationId,
        locationHierarchy:
          locationHierarchy === '' ? partLocation.locationHierarchy : locationHierarchy,
        locationTypeCode:
          locationTypeCode === '' ? partLocation.locationTypeCode : locationTypeCode,
      });
    setIsEdit(false);
    setIsShown(false);
  };

  const handleCancel = () => {
    setTextValue(serial);
    setIsEdit(false);
    setIsShown(false);
  };

  return (
    <Grid
      container
      display="flex"
      width="100%"
      alignItems="center"
      onMouseEnter={() => isEditable && !alwaysShowEditIcon && setIsShown(true)}
      onMouseLeave={() => isEditable && !alwaysShowEditIcon && setIsShown(false)}
    >
      {isEdit ? (
        <Grid
          size={{
            xs: 12,
            md: 8,
          }}
        >
          <LiveSearchBox
            title={title}
            timeOffset={400}
            value={locationName}
            onClearValue={() => ''}
            renderItem={(props: HTMLAttributes<HTMLLIElement>, option: LocationNodeData) => {
              return (
                <LiveSearchListItem {...props}>
                  <LocationSearchItem data={option} isUpdatePartLocation={isUpdatePartLocation} />
                </LiveSearchListItem>
              );
            }}
            onChange={(obj: LocationNodeData) => {
              setLocationId(obj?.id);

              if (obj?.name.includes(SearchCode.AM_PID)) {
                setLocationHierarchy(obj?.locationHierarchy || '');
                setLocationName(obj?.serial1 || '');
                setLocationTypeCode('part');
                setTextValue(obj?.name);
              } else if (obj?.name.includes(SearchCode.AM_LID)) {
                setLocationHierarchy(obj?.parentTree || '');
                setLocationName(obj?.code || '');
                setLocationTypeCode('node');
              } else {
                setLocationHierarchy(obj?.parentTree || '');
                setLocationName(obj?.name || '');
                setLocationTypeCode(obj?.partType || obj?.serial1 ? 'part' : 'node');
                setTextValue(obj?.name);
                setId(obj?.id);
              }
            }}
            onApiInvoke={async (name: string) => {
              if (name.length < 3 || (name.toLowerCase().includes('am-') && name.length < 8)) {
                return [];
              }

              if (isSerial) {
                return await searchParts.mutateAsync({ id: partLocation.id, name });
              }

              return await searchNodeParts.mutateAsync({
                id: partLocation.id,
                name: name.replace('#', '%23'),
                nodeIdTree: partLocation.locationHierarchy,
                includeSiblings: true,
                applicableNodeIds,
              });
            }}
          />
        </Grid>
      ) : (
        <Grid>
          {isSerial ? (
            <Typography>{serial ? serial : '-'}</Typography>
          ) : (
            <>
              <Typography variant={TypographyVariantProps.Subtitle2} textColor="grey">
                {partLocation.nodeList.map((node: Node, index: number) => (
                  <>
                    {node.nodeTypeCode === NodeType.Client ||
                    node.nodeTypeCode === NodeType.Contract ||
                    node.nodeTypeCode === NodeType.Brand ? (
                      <>
                        {isClientHierarchy ? (
                          <>
                            <PermissionLink
                              permission="configure.client_structure.clients_or_contracts.edit"
                              href={`/configure/clients/${node.nodeTypeCode}/${node.id}`}
                            >
                              {node.name}
                            </PermissionLink>
                            &nbsp;/&nbsp;
                          </>
                        ) : (
                          <>{node.name}&nbsp;/&nbsp;</>
                        )}
                      </>
                    ) : node.nodeTypeCode === PartLocationType.Part ? (
                      <>
                        <PermissionLink permission="part.search" href={`/parts/${node.id}`}>
                          {node.name}
                        </PermissionLink>
                        &nbsp;{index !== partLocation.nodeList.length - 1 ? '/' : ''}&nbsp;
                      </>
                    ) : (
                      <>
                        {isLocation ? (
                          <>
                            <PermissionLink
                              permission="structure.edit.view_structure_location"
                              href={`/structure/${node.id}`}
                            >
                              {node.name}
                            </PermissionLink>
                            &nbsp;{index !== partLocation.nodeList.length - 1 ? '/' : ''}&nbsp;
                          </>
                        ) : (
                          <>{node.name}&nbsp;/&nbsp;</>
                        )}
                      </>
                    )}
                  </>
                ))}
              </Typography>
              <Typography variant={TypographyVariantProps.Subtitle2}>
                <b>{displayNodeTypeName(partLocation.nodeTypeName)}</b> &nbsp;&nbsp;{partLocation.name}
              </Typography>
            </>
          )}
        </Grid>
      )}
      {isEdit && (
        <Grid>
          <Button
            variant={ButtonVariantProps.Primary}
            color={ButtonColorProps.Success}
            onClick={handleOnSubmit}
            size={ButtonSizeProps.Small}
            className="extra-small-button"
          >
            Save
          </Button>
          <Button
            variant={ButtonVariantProps.Secondary}
            color={ButtonColorProps.Error}
            onClick={handleCancel}
            size={ButtonSizeProps.Small}
            className="extra-small-button"
          >
            Cancel
          </Button>
        </Grid>
      )}
      {!isEdit && isEditable && (alwaysShowEditIcon || isShown) && (
        <Grid>
          {alwaysShowEditIcon ? (
            <IconButton
              size="small"
              onClick={() => setIsEdit(true)}
              aria-label="Edit location"
              sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}
            >
              <EditOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          ) : (
            <Button
              variant={ButtonVariantProps.Icon}
              size={ButtonSizeProps.Small}
              onClick={() => setIsEdit(true)}
            >
              <EditOutlined fontSize="small" color={ButtonColorProps.Warning} />
            </Button>
          )}
        </Grid>
      )}
    </Grid>
  );
};
